/** @jsxImportSource @emotion/react */
import React, { useCallback, useState, useEffect } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Modal, Alert } from 'antd'
import dayjs from 'dayjs'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import RangePicker from 'src/components/ts-components/RangePicker'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getErrorMessage } from '@sportninja/common/utils/utils'

const Separator = () => {
  return (
    <div
      css={css`
        border-bottom: 1px solid #3f4753;
      `}
    />
  )
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type CloneCompetitionModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit?: (values: any) => Promise<any>
  schedule?: any
  isLoading?: boolean
}

const CloneCompetitionModal: React.FC<CloneCompetitionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  schedule,
  isLoading = false,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const { openModal, closeModal } = useModalScroll()

  // Form state
  const [name, setName] = useState('')
  const [abbreviation, setAbbreviation] = useState('')
  const [competitionDateRange, setCompetitionDateRange] = useState({
    starts_at: '',
    ends_at: '',
  })
  const [playoffCompetition, setPlayoffCompetition] = useState(false)
  const [isRegistrationSchedule, setIsRegistrationSchedule] = useState(true)

  // Form validation
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState<Record<string, string>>({})
  const [apiError, setApiError] = useState<string>('')

  /**
   * Initialize form with schedule data
   */
  useEffect(() => {
    if (schedule && isOpen) {
      setName(`Copy of ${schedule?.name_full || schedule?.name || ''}`)
      setAbbreviation(schedule.abbreviation || '')

      // Set date range to current date if no schedule dates
      const today = dayjs().format('YYYY-MM-DD')
      setCompetitionDateRange({
        starts_at: schedule.starts_at
          ? dayjs(schedule.starts_at).utc().format('YYYY-MM-DD')
          : today,
        ends_at: schedule.ends_at
          ? dayjs(schedule.ends_at).utc().format('YYYY-MM-DD')
          : today,
      })

      setPlayoffCompetition(schedule.is_tournament || false)
      setIsRegistrationSchedule(schedule.is_registration_on || false)
    }
  }, [schedule, isOpen])

  /**
   * Reset form state when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      setName('')
      setAbbreviation('')
      setCompetitionDateRange({ starts_at: '', ends_at: '' })
      setPlayoffCompetition(false)
      setIsRegistrationSchedule(false)
      setFieldErrors([])
      setCustomError({})
      setApiError('')
    }
  }, [isOpen])

  /**
   * Handle modal scroll
   */
  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  /**
   * Validate form fields
   */
  const validateForm = useCallback(() => {
    const errors: string[] = []
    const customErrors: Record<string, string> = {}

    if (!name.trim()) {
      errors.push('name')
      customErrors.name = 'Competition name is required'
    } else if (name.trim().length > 64) {
      errors.push('name')
      customErrors.name = 'Competition name must be at most 64 characters'
    }

    if (!abbreviation.trim()) {
      errors.push('abbreviation')
      customErrors.abbreviation = 'Abbreviation is required'
    } else if (abbreviation.length > 8) {
      errors.push('abbreviation')
      customErrors.abbreviation = 'Abbreviation must be 8 characters or less'
    }

    if (!competitionDateRange.starts_at) {
      errors.push('competitionDateRange')
      customErrors.competitionDateRange = 'Start date is required'
    }

    if (!competitionDateRange.ends_at) {
      errors.push('competitionDateRange')
      customErrors.competitionDateRange = 'End date is required'
    }

    if (competitionDateRange.starts_at && competitionDateRange.ends_at) {
      const startDate = dayjs(competitionDateRange.starts_at)
      const endDate = dayjs(competitionDateRange.ends_at)

      if (endDate.isBefore(startDate)) {
        errors.push('competitionDateRange')
        customErrors.competitionDateRange = 'End date must be after start date'
      }
    }

    setFieldErrors(errors)
    setCustomError(customErrors)

    return errors.length === 0
  }, [name, abbreviation, competitionDateRange])

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async () => {
    // Clear any previous API errors before submission
    setApiError('')

    if (!validateForm()) {
      return
    }

    const formData = {
      name: name.trim(),
      name_full: name.trim(),
      abbreviation: abbreviation.trim(),
      starts_at: competitionDateRange.starts_at,
      ends_at: competitionDateRange.ends_at,
      is_tournament: playoffCompetition,
      is_registration_schedule: isRegistrationSchedule,
    }

    if (onSubmit) {
      try {
        await onSubmit(formData)
        onClose()
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        setApiError(errorMessage)
        console.error('Error submitting form:', error)
      }
    }
  }, [
    validateForm,
    name,
    abbreviation,
    competitionDateRange,
    playoffCompetition,
    isRegistrationSchedule,
    onSubmit,
    onClose,
  ])

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Renders the modal header
   */
  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Clone {isCanlan ? 'Season' : 'Competition'}
      </Text>
    )
  }, [])

  /**
   * Renders the modal footer
   */
  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 100%;
          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        <Button
          text='Cancel'
          onClick={handleClose}
          variant='secondary'
          disabled={isLoading}
        />
        <Button
          text={isLoading ? 'Submitting...' : 'Submit'}
          onClick={handleSubmit}
          variant='primary'
          disabled={isLoading}
          isLoading={isLoading}
        />
      </div>
    )
  }, [handleClose, handleSubmit, isLoading])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '570px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 16px;
        }
      `}
      width={isMobile ? '100%' : 570}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
          overflow-y: auto;
          flex: 1;
        `}
      >
        {/* Competition Name and Abbreviation */}
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
            ${isMobile &&
            css`
              flex-direction: column;
            `}
          `}
        >
          {/* Competition Name */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              ${isMobile &&
              css`
                width: 100%;
              `}
            `}
          >
            <TextInput
              name='name'
              value={name}
              setValue={setName}
              fieldErrors={fieldErrors}
              customError={customError}
              label={`${isCanlan ? 'Season' : 'Competition'} Name *`}
              placeholder={`Enter ${isCanlan ? 'season' : 'competition'} name`}
              maxLength={64}
            />
          </div>

          {/* Abbreviation */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              ${isMobile &&
              css`
                width: 100%;
              `}
            `}
          >
            <TextInput
              name='abbreviation'
              value={abbreviation}
              setValue={setAbbreviation}
              fieldErrors={fieldErrors}
              customError={customError}
              label='Abbreviation (Max 8 characters) *'
              placeholder='Enter abbreviation'
              maxLength={8}
            />
          </div>
        </div>

        {/* Start and End Date */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
          `}
        >
          <RangePicker
            name='competitionDateRange'
            starts_at={competitionDateRange.starts_at}
            ends_at={competitionDateRange.ends_at}
            customError={customError}
            fieldErrors={fieldErrors}
            label='Start and End Date *'
            onChange={(e) => {
              if (e) {
                const [starts_at, ends_at] = e
                setCompetitionDateRange({
                  starts_at: dayjs(starts_at).utc().format('YYYY-MM-DD'),
                  ends_at: dayjs(ends_at).utc().format('YYYY-MM-DD'),
                })
              }
            }}
          />
        </div>

        <Separator />

        {/* Registration Schedule */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-self: stretch;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              width: 100%;
            `}
          >
            <SwitchInput
              title={`Registration ${isCanlan ? 'Season' : 'Competition'}`}
              subtitle={
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Turn this ON if the cloned{' '}
                  {isCanlan ? 'season' : 'competition'} will be used for
                  registration purposes.
                </Text>
              }
              value={isRegistrationSchedule}
              disabled={playoffCompetition}
              onChange={(value) => {
                setIsRegistrationSchedule(value)
                // If turning Registration ON, turn Playoff OFF
                if (value) {
                  setPlayoffCompetition(false)
                }
              }}
            />
          </div>

          {/* Learn more link */}
          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              width: 100%;
            `}
          >
            <div
              css={css`
                cursor: pointer;
                &:hover {
                  text-decoration: underline;
                }
              `}
              onClick={() => {
                window.open(
                  'https://support.sportninja.com/registration',
                  '_blank'
                )
              }}
            >
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.PRIMARY}
              >
                Learn More
              </Text>
            </div>
          </div>
        </div>

        {/* Playoff Competition */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-self: stretch;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              width: 100%;
            `}
          >
            <SwitchInput
              title={`Playoff ${isCanlan ? 'Season' : 'Competition'}`}
              subtitle={
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Turn this ON to create a playoff that continues from the
                  original {isCanlan ? 'season' : 'competition'}.
                  <br />
                  Want a simpler setup? Leave it OFF to use the basic version.
                </Text>
              }
              value={playoffCompetition}
              disabled={isRegistrationSchedule}
              onChange={(value) => {
                setPlayoffCompetition(value)
                // If turning Playoff ON, turn Registration OFF
                if (value) {
                  setIsRegistrationSchedule(false)
                }
              }}
            />
          </div>

          {/* Learn more link */}
          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              width: 100%;
            `}
          >
            <div
              css={css`
                cursor: pointer;
                &:hover {
                  text-decoration: underline;
                }
              `}
              onClick={() => {
                window.open(
                  'https://support.sportninja.com/league-competition-management-6#tournaments-playoffs',
                  '_blank'
                )
              }}
            >
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.PRIMARY}
              >
                Learn More
              </Text>
            </div>
          </div>
        </div>

        {/* API Error Alert */}
        {apiError && (
          <div
            css={css`
              margin-top: 16px;
            `}
          >
            <Alert
              message={apiError}
              type='error'
              showIcon
              onClose={() => setApiError('')}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CloneCompetitionModal
